import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import RelocationAffiliate from '../components/RelocationAffiliate';
import RelocationCTA from '../components/RelocationCTA';
import { RelocationBreadcrumb, RelocationSiblingLinks } from '../components/RelocationNav';

const neighborhoods = [
  { name: 'Condesa', description: 'Tree-lined streets, art deco architecture, parks, and the city\'s best café culture. The quintessential expat neighborhood.', rent: 'Avg 1BR: $900–$1,600/mo' },
  { name: 'Roma Norte', description: 'Creative and culinary hub. Galleries, boutique shops, and some of the best restaurants in Latin America. Vibrant nightlife.', rent: 'Avg 1BR: $800–$1,400/mo' },
  { name: 'Polanco', description: 'The upscale district. Luxury shopping, fine dining, museums (Soumaya, Jumex), and corporate offices. Best for executives.', rent: 'Avg 1BR: $1,200–$2,200/mo' },
  { name: 'Santa Fe', description: 'Modern business district on the west side. Corporate headquarters, malls, and newer high-rise apartments. Car-dependent.', rent: 'Avg 1BR: $900–$1,500/mo' },
  { name: 'Coyoacán', description: 'Bohemian university quarter. Frida Kahlo\'s neighborhood. Colonial plazas, markets, and a more local, less touristy feel.', rent: 'Avg 1BR: $600–$1,100/mo' },
  { name: 'Narvarte', description: 'Up-and-coming residential area. More affordable than Condesa/Roma with improving restaurant scene and good metro access.', rent: 'Avg 1BR: $600–$1,000/mo' },
];

const costs = [
  { label: 'Rent (1BR, city center)', value: '$900–$1,400/mo' },
  { label: 'Groceries', value: '$250–$400/mo' },
  { label: 'Dining out (2 people, mid-range)', value: '$20–$40' },
  { label: 'Transportation (metro + Uber)', value: '$50–$120/mo' },
  { label: 'Utilities (electric, water, internet)', value: '$80–$130/mo' },
  { label: 'Health insurance (private)', value: '$100–$250/mo' },
];

const faqs = [
  { q: 'Is Mexico City safe for Americans?', a: 'Yes, with normal urban precautions. Neighborhoods like Condesa, Roma, Polanco, and Coyoacán are very safe with low crime rates comparable to major US cities. Avoid displaying expensive jewelry, use ride-hailing apps at night, and stick to well-known areas. Millions of Americans visit and live in CDMX safely every year.' },
  { q: 'What is the cost of living in Mexico City vs the US?', a: 'Mexico City is roughly 50–60% cheaper than major US cities. A single person can live comfortably on $2,000–$3,500/month including rent in a nice neighborhood, dining out regularly, and Uber transportation. Couples should budget $3,200–$5,500/month.' },
  { q: 'What are the best neighborhoods for American expats?', a: 'Condesa and Roma Norte are the most popular - walkable, safe, full of restaurants and cafés. Polanco for luxury. Coyoacán for culture and affordability. Narvarte for value. Each has a distinct personality; we recommend spending a week in 2–3 neighborhoods before committing to a lease.' },
  { q: 'What is the climate like in Mexico City?', a: 'Spring-like year-round thanks to 7,350 feet of elevation. Daytime temperatures average 70–80°F. Nights are cool (45–55°F in winter). Rainy season (June–October) brings afternoon showers but mornings are usually clear. No extreme heat, no humidity - it\'s one of the most comfortable climates in the world.' },
  { q: 'How do I get to Mexico City from the US?', a: 'CDMX has one of the busiest airports in Latin America (MEX) with direct flights from every major US city. Flights from Dallas and Houston are about 2.5–3 hours. From LA, about 4 hours. Multiple airlines (American, United, Delta, Volaris, Aeromexico) offer dozens of daily flights.' },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const CDMX_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1702146829082-903ae8ceca28?w=1920&q=80',
  neighborhood: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=1200&q=80',
  lifestyle: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=1200&q=80',
  architecture: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=1200&q=80',
};

const CDMXRelocation = () => (
  <div className="min-h-screen bg-[#FAF7F2]">
    <SEO
      title="Moving to Mexico City: Complete American's Relocation Guide (2025)"
      description="The complete guide to relocating to Mexico City from the US. Visas, housing, banking, healthcare, and vetted resources by Mundalo."
      path="/relocation/cdmx"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    <Header currentPage="relocation" />

    {/* Hero with Image */}
    <section className="relative">
      <div className="h-[70vh] lg:h-screen lg:max-h-none relative overflow-hidden">
        <img 
          src={CDMX_IMAGES.hero} 
          alt="Mexico City Reforma skyline at night" 
          className="w-full h-full object-cover object-center block"
          data-testid="cdmx-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/80 via-[#FFFFFF]/40 to-transparent" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 pb-8 lg:pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RelocationBreadcrumb cityName="Mexico City" />
          <h1 className="text-3xl lg:text-5xl text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="cdmx-hero-title">
            Moving to Mexico City: The Complete American's Relocation Guide
          </h1>
          <p className="text-base lg:text-lg text-white/80 max-w-2xl leading-relaxed mb-8">
            The cosmopolitan capital. World-class infrastructure, 3 hours from Dallas, Central Time zone. The most popular destination for American expats, remote workers, and executives relocating to Mexico.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/relocation/blueprint"
              className="inline-flex items-center gap-3 bg-[#E8A830] text-white px-8 py-4 text-sm font-semibold rounded-lg hover:bg-[#a8511f] transition-all group"
              data-testid="hero-cta"
            >
              Get Your Relocation Plan
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/relocation"
              className="inline-flex items-center gap-2 text-sm font-medium text-white border border-white/30 px-6 py-4 rounded-lg hover:bg-white hover:text-[#FFFFFF] transition-all"
            >
              View All Cities
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Why CDMX */}
    <section className="py-20 lg:py-28">
      <div className="max-w-[680px] mx-auto px-6 lg:px-12">
        <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-10" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="why-heading">
          Why Mexico City?
        </h2>
        <div className="space-y-6 text-[#666] text-lg leading-relaxed">
          <p>
            Mexico City is the undisputed capital of the American-to-Mexico relocation movement. A metropolitan area of 22 million people, it offers world-class dining, museums, nightlife, and infrastructure - at a fraction of the cost of New York, LA, or San Francisco. The Central Time zone and 3-hour flights from Texas make it practical for professionals who need to stay connected to US business.
          </p>
          <p>
            The expat community has exploded since 2020, with neighborhoods like Condesa and Roma Norte becoming global remote-work hubs. But CDMX is far more than a digital nomad destination - it's a thriving metropolis with deep cultural roots, excellent private healthcare, international schools, and a sophistication that surprises every first-time visitor.
          </p>
          <p>
            The altitude (7,350 feet) gives CDMX a spring-like climate year-round - no extreme heat, no humidity, and cool evenings. The food scene is arguably the best in the Americas. And the cost of living allows a lifestyle that would require $150K+ in a major US city on a $60–80K budget here.
          </p>
        </div>
      </div>
    </section>

    {/* Image Gallery */}
    <section className="py-12 bg-white border-t border-b border-[#e0dbd4]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative h-64 overflow-hidden rounded-lg">
            <img src={CDMX_IMAGES.neighborhood} alt="Roma Norte cafe street in Mexico City" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <p className="text-white text-sm font-medium">Roma Norte</p>
            </div>
          </div>
          <div className="relative h-64 overflow-hidden rounded-lg">
            <img src={CDMX_IMAGES.lifestyle} alt="Polanco luxury restaurant in Mexico City" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <p className="text-white text-sm font-medium">Polanco Dining</p>
            </div>
          </div>
          <div className="relative h-64 overflow-hidden rounded-lg">
            <img src={CDMX_IMAGES.architecture} alt="Condesa art deco architecture in Mexico City" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <p className="text-white text-sm font-medium">Condesa Architecture</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Neighborhoods */}
    <section className="py-20 lg:py-24 bg-white border-t border-[#e0dbd4]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="neighborhoods-heading">
          Best Neighborhoods in Mexico City
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {neighborhoods.map((n) => (
            <div key={n.name} className="p-6 border border-[#e0dbd4] bg-[#FAF7F2] rounded-lg" data-testid={`neighborhood-${n.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <h3 className="text-[#FFFFFF] font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{n.name}</h3>
              <p className="text-[#666] text-sm mb-3">{n.description}</p>
              <p className="text-[#E8A830] text-xs font-semibold">{n.rent}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Cost of Living */}
    <section className="py-20 lg:py-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="cost-heading">
          Cost of Living in Mexico City
        </h2>
        <div className="space-y-4 mb-10">
          {costs.map((c) => (
            <div key={c.label} className="flex justify-between items-center py-3 border-b border-[#e0dbd4]">
              <span className="text-[#666] text-sm">{c.label}</span>
              <span className="text-[#FFFFFF] text-sm font-medium">{c.value}</span>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="p-6 border border-[#E8A830]/30 bg-[#E8A830]/5 rounded-lg">
            <p className="text-[#E8A830] text-xs font-semibold mb-1">Single Person Monthly</p>
            <p className="text-[#FFFFFF] text-xl font-light">$2,000–$3,500/mo</p>
          </div>
          <div className="p-6 border border-[#E8A830]/30 bg-[#E8A830]/5 rounded-lg">
            <p className="text-[#E8A830] text-xs font-semibold mb-1">Couple Monthly</p>
            <p className="text-[#FFFFFF] text-xl font-light">$3,200–$5,500/mo</p>
          </div>
        </div>
      </div>
    </section>

    {/* Affiliate Resources */}
    <RelocationAffiliate />

    {/* Consultation CTA */}
    <RelocationCTA />

    {/* FAQ */}
    <section className="py-20 lg:py-24 bg-white border-t border-[#e0dbd4]">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="faq-heading">
          Frequently Asked Questions
        </h2>
        <div className="space-y-8">
          {faqs.map((f, i) => (
            <div key={i} data-testid={`faq-${i}`}>
              <h3 className="text-[#FFFFFF] font-semibold mb-3">{f.q}</h3>
              <p className="text-[#666] text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* iter 150 - Reciprocal link to the vetted professional directory. */}
    <section
      className="py-12 lg:py-16 bg-white border-t border-[#e0dbd4]"
      data-testid="city-guide-directory-block"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-6 p-6 md:p-7 bg-[#F9F6EF] border border-[#e0dbd4] rounded-2xl">
          <div className="flex-1">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1.5">
              Vetted local professionals
            </p>
            <p className="text-[#0A1F2E] text-base md:text-lg leading-snug">
              Need a Mexico City immigration attorney or tax advisor?{' '}
              <span className="text-[#0A1F2E]/70">See our vetted directory.</span>
            </p>
          </div>
          <a
            href="/find-a-professional/mexico-city"
            data-testid="city-guide-directory-cta"
            className="flex-none inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-[0.14em] hover:bg-[#163448] transition-colors"
          >
            Open the directory →
          </a>
        </div>
      </div>
    </section>

    {/* Sibling City Links */}
    <RelocationSiblingLinks currentPath="/relocation/cdmx" />

    <Footer />
  </div>
);

export default CDMXRelocation;
